import {
  AmountCell,
  Badge,
  ClipboardDocumentListIcon,
  formatTimeDate,
  IdCell,
  NameCell,
  PhoneCell,
  success,
} from "@";
import type { ColumnDef } from "@tanstack/react-table";
import type { GiftOrder } from "../types";
import EditableTextCell from "./EditableTextCell";

type EditableNameField = "giftSenderName" | "giftRecieverName";

interface CreateTableColumnsArgs {
  canEdit: boolean;
  onSaveName: (
    row: GiftOrder,
    patch: Partial<Pick<GiftOrder, EditableNameField>>,
  ) => Promise<void>;
}

const copyLink = async (link: string) => {
  await navigator.clipboard.writeText(link);
  await success("تم نسخ الرابط");
};

// Feature-specific: small "copy gift link" pill — kept inline because it's
// only used here and has its own clipboard side-effect.
const CopyLinkCell = ({ link }: { link?: string }) => {
  if (!link) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  }
  return (
    <button
      type="button"
      onClick={() => void copyLink(link)}
      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-200/80 transition-colors hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-800/40 dark:hover:bg-emerald-900/40"
    >
      <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
      نسخ
    </button>
  );
};

const PaymentTypeBadge = ({ type }: { type: number }) => (
  <Badge
    title={type === 0 ? "بطاقة" : "تحويل بنكي"}
    color={type === 0 ? "default" : "dark"}
  />
);

// DateTime cell — uses the time-aware formatter. Mirrors DateCell styling
// so it visually rhymes with date-only columns elsewhere.
const DateTimeCell = ({ value }: { value?: string | null }) => (
  <span className="text-sm tabular-nums text-gray-700 dark:text-gray-300">
    {value ? formatTimeDate(value) : "—"}
  </span>
);

// Editable name with the existing inline-edit affordance when permitted,
// or a plain bold name otherwise — typography stays consistent with the
// rest of the table either way.
const buildEditableNameColumn = (
  field: EditableNameField,
  header: string,
  { canEdit, onSaveName }: CreateTableColumnsArgs,
): ColumnDef<GiftOrder> => ({
  id: field,
  header,
  size: 160,
  cell: canEdit
    ? ({ row }) => (
        <EditableTextCell
          value={row.original[field]}
          id={`${row.original.orderItemId}-${field}`}
          onSave={(v) =>
            onSaveName(row.original, { [field]: v } as Partial<
              Pick<GiftOrder, EditableNameField>
            >)
          }
          className="hover:font-bold"
        />
      )
    : ({ row }) => <NameCell name={row.original[field]} />,
});

export const createTableColumns = (
  args: CreateTableColumnsArgs,
): ColumnDef<GiftOrder>[] => [
  {
    id: "orderId",
    header: "العملية",
    size: 90,
    cell: ({ row }) => <IdCell value={row.original.orderId} />,
  },
  {
    id: "giftProjectLink",
    header: "رابط الإهداء",
    size: 100,
    cell: ({ row }) => <CopyLinkCell link={row.original.giftProjectLink} />,
  },
  {
    id: "customerName",
    header: "إسم المتبرع",
    size: 180,
    cell: ({ row }) => <NameCell name={row.original.customerName} />,
  },
  {
    id: "customerPhone",
    header: "رقم الهاتف",
    size: 130,
    cell: ({ row }) => <PhoneCell phone={row.original.customerPhone} />,
  },
  buildEditableNameColumn("giftSenderName", "إسم المرسل", args),
  buildEditableNameColumn("giftRecieverName", "إسم المهدي إليه", args),
  {
    id: "giftRecieverPhone",
    header: "جوال المهدي إليه",
    size: 130,
    cell: ({ row }) => <PhoneCell phone={row.original.giftRecieverPhone} />,
  },
  {
    id: "giftProjectName",
    header: "المشروع",
    size: 200,
    cell: ({ row }) => <NameCell name={row.original.giftProjectName} />,
  },
  {
    id: "amount",
    header: "مبلغ التبرع",
    size: 100,
    cell: ({ row }) => (
      <AmountCell value={row.original.amount} tone="emerald" />
    ),
  },
  {
    id: "paymentType",
    header: "وسيلة التبرع",
    size: 100,
    cell: ({ row }) => <PaymentTypeBadge type={row.original.paymentType} />,
  },
  {
    id: "createdOn",
    header: "التاريخ",
    size: 110,
    cell: ({ row }) => <DateTimeCell value={row.original.createdOn} />,
  },
];
